<template>
    <div class="create-task-process-modal">
        <ProcessModal
            :is-open="isOpen && !showOutcomeConfig && !showOutcomeEditor"
            :title="$t('funnel.campaign.create.task.title')"
            :summary-text="$t('funnel.campaign.create.task.summary')"
            show-save
            :process-tip-text="$t('funnel.campaign.create.task.tip')"
            :process-tip-link="$t('funnel.campaign.create.task.more.link')"
            @close-modal="closeModal"
            @update-config="updateConfig"
        >
            <template #processParameters>
                <div class="merge-field-container">
                    <CampaignBuilderMergeableTextArea
                        v-model="taskTitle"
                        data-qa="create-task-title-input"
                        :has-legacy-format="true"
                        :funnel-id="funnelId"
                        :has-error="invalidTaskTitle"
                        :default-place-holder="$t('funnel.campaign.create.task.title.label')"
                        :merge-field-categories="mergeFields"
                        class="create-task-merge-field-input"
                    />
                </div>
                <div v-if="invalidTaskTitle" class="error-message">
                    <span data-qa="invalid-task-title">
                        {{ $t('funnel.campaign.create.task.required') }}
                    </span>
                </div>
                <div class="merge-field-container">
                    <CampaignBuilderMergeableTextArea
                        v-model="taskBody"
                        data-qa="create-task-description"
                        type="text"
                        :has-legacy-format="true"
                        :funnel-id="funnelId"
                        :merge-field-categories="mergeFields"
                        :default-place-holder="$t('funnel.campaign.create.task.description')"
                        class="create-task-merge-field-input"
                    />
                </div>
                <DsMultiselect
                    v-model="taskType"
                    data-qa="create-task-type"
                    class="create-task-merge-field-input"
                    :placeholder="taskTypePlaceholder"
                    :force-invalid="invalidTaskType"
                    :options="taskTypeOptions"
                    bind-value-only
                    required
                >
                    <template #help>
                        <span v-if="invalidTaskType" data-qa="invalid-task-type">
                            {{ $t('funnel.campaign.create.task.required') }}
                        </span>
                    </template>
                </DsMultiselect>
                <DsMultiselect
                    v-model="taskPriority"
                    data-qa="create-task-priority"
                    class="create-task-priority"
                    :placeholder="$t('funnel.campaign.create.task.priority')"
                    :force-invalid="invalidTaskPriority"
                    :options="priorityOptions"
                    bind-value-only
                    required
                >
                    <template #help>
                        <span v-if="invalidTaskPriority" data-qa="invalid-task-priority">
                            {{ $t('funnel.campaign.create.task.required') }}
                        </span>
                    </template>
                </DsMultiselect>
                <DsMultiselect
                    v-model="taskOutcome"
                    data-qa="create-task-outcome"
                    class="create-task-outcome"
                    :placeholder="$t('funnel.campaign.create.task.outcomes')"
                    :add-text="addOutcomeLabel"
                    :options="taskOutcomeOptions"
                    :no-results-label="$t('funnel.campaign.create.task.empty.outcomes')"
                    bind-value-only
                    multiple
                    searchable
                    show-search-icon
                    :allow-add="!outcomeLimitReached"
                    @add="openAddNewOutcome"
                    @updateSearch="handleInputChange"
                >
                    <template #help>
                        <DsLink
                            @click="manageTaskOutcomes"
                        >
                            {{ $t('funnel.campaign.create.task.outcome.manage') }}
                        </DsLink>
                    </template>
                </DsMultiselect>
                <DsCheckbox
                    v-model="taskAssignToOwner"
                    data-qa="create-task-assign-owner"
                    class="create-task-assign-owner"
                    :label="$t('funnel.campaign.create.task.assign')"
                />
                <DsMultiselect
                    v-model="taskAssignToBackup"
                    data-qa="create-task-backup"
                    class="create-task-backup"
                    :placeholder="$t('funnel.campaign.create.task.backup')"
                    :force-invalid="invalidTaskBackup"
                    :options="usersOptions"
                    bind-value-only
                    searchable
                    show-search-icon
                    required
                >
                    <template #help>
                        <span v-if="invalidTaskBackup" data-qa="invalid-task-backup">
                            {{ $t('funnel.campaign.create.task.required') }}
                        </span>
                    </template>
                </DsMultiselect>
                <DsCheckbox
                    v-model="taskNotifyOwner"
                    data-qa="create-task-notify-owner"
                    class="create-task-notify-owner"
                    :label="$t('funnel.campaign.create.task.notify')"
                />
                <div class="create-task-due">
                    <span class="create-task-due-title">
                        {{ $t('funnel.campaign.create.task.due') }}
                    </span>
                    <DsInputField
                        v-model="taskDaysTillDue"
                        data-qa="create-task-days-till-due"
                        class="create-task-days-due"
                        name="create-task-days-due"
                        type="number"
                        :step="1"
                        :min="1"
                        :max="9999"
                    />
                    <span class="create-task-due-label">
                        {{ $t('funnel.campaign.create.task.due.days') }}
                    </span>
                    <DsDatePicker
                        v-model="taskDueAt"
                        data-qa="create-task-due-at"
                        class="create-task-due-at"
                        name="create-task-due-at"
                        :label="$t('funnel.campaign.create.task.due.time')"
                        time-only
                    />
                </div>
                <DsMultiselect
                    v-model="taskNotify"
                    data-qa="create-task-notify"
                    :placeholder="$t('funnel.campaign.create.task.notify.user')"
                    bind-value-only
                    multiple
                    show-search-icon
                    searchable
                    :options="usersOptions"
                />
                <DsMultiselect
                    v-model="taskReminder"
                    data-qa="create-task-reminder"
                    class="create-task-reminder"
                    :placeholder="$t('funnel.campaign.create.task.reminder')"
                    bind-value-only
                    :options="reminderOptions"
                />
            </template>
        </ProcessModal>
        <TaskOutcomeManager
            v-if="showOutcomeConfig"
            :is-open="showOutcomeConfig"
            @close-config-modal="closeConfigModal"
        />
        <OutcomeEditor
            v-if="showOutcomeEditor"
            :suggested-name="outcomeToAdd"
            :editing="false"
            :is-open="showOutcomeEditor"
            :category-list="outcomeCategoryOptions"
            @close-editor="closeEditor"
            @close-reload="closeEditorAndReload"
        />
    </div>
</template>

<script>
import ProcessModal from '../processModal/ProcessModal.vue';
import TaskOutcomeManager from '../../../../configuration/taskOutcomes/TaskOutcomeManager.vue';
import OutcomeEditor from '../../../../configuration/taskOutcomes/OutcomeEditor.vue';
import CampaignBuilderMergeableTextArea from '../../../whens/campaignBuilderMergeableTextArea/CampaignBuilderMergeableTextArea.vue';

import {
    getContactActionOptions,
    getGroupMembers,
    getReminderOptions,
    getTaskOutcomeOptions,
    getPriorityOptions,
    getMergeFieldsForCB,
} from '../../../../configuration/task.flow.api';
import {
    getTaskOutcomeCategoryList,
} from '../../../../configuration/task.outcome.api';

export const TASK_OUTCOME_LIMIT = 100;
export const DEFAULT_MILITARY_TIME = '0800';

export default {
    DEFAULT_MILITARY_TIME,
    TASK_OUTCOME_LIMIT,
    components: {
        OutcomeEditor,
        TaskOutcomeManager,
        ProcessModal,
        CampaignBuilderMergeableTextArea,
    },
    props: {
        funnelId: {
            type: Number,
            default: 0,
        },
        isOpen: {
            type: Boolean,
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                    taskType: '',
                    taskTitle: '',
                    taskBody: '',
                    taskAssignToOwner: false,
                    taskAssignToBackup: '',
                    taskDaysTillDue: 0,
                    taskDueAt: this.$options.DEFAULT_MILITARY_TIME,
                    taskPriority: '',
                    taskNotifyOwner: false,
                    taskNotify: [],
                    taskReminder: '',
                    taskOutcome: [],
                    outcomeToAdd: '',
                },
            }),
        },
    },
    emits: ['close-modal', 'update-cell-value'],
    data() {
        return {
            mergeFields: [],
            isMergeFieldDropdownOpen: false,
            taskType: '',
            taskTitle: '',
            taskBody: '',
            taskAssignToOwner: false,
            taskAssignToBackup: '',
            taskDaysTillDue: 0,
            taskDueAt: this.$options.DEFAULT_MILITARY_TIME,
            taskPriority: '',
            taskNotifyOwner: false,
            taskNotify: [],
            taskReminder: '',
            taskOutcome: [],
            taskTypeOptions: [],
            taskOutcomeOptions: [],
            usersOptions: [],
            reminderOptions: [],
            priorityOptions: [],
            invalidTaskTitle: false,
            invalidTaskType: false,
            invalidTaskBackup: false,
            invalidTaskPriority: false,
            newOutcomeInput: '',
            showOutcomeConfig: false,
            showOutcomeEditor: false,
            outcomeCategoryOptions: [],
        };
    },

    computed: {
        outcomeLimitReached() {
            let totalCount = 0;

            this.taskOutcomeOptions.forEach((item) => {
                if (item.options?.length) {
                    totalCount += item.options.length;
                }
            });

            return totalCount >= TASK_OUTCOME_LIMIT;
        },

        taskTypePlaceholder() {
            return this.taskType === ''
                ? this.$t('funnel.campaign.create.task.placeholder')
                : this.$t('funnel.campaign.create.task.type');
        },

        addOutcomeLabel() {
            return this.newOutcomeInput === ''
                ? this.$t('funnel.campaign.create.task.outcome.add')
                : `${this.$t('funnel.campaign.create.task.outcome.prefix')}${this.newOutcomeInput}${this.$t('funnel.campaign.create.task.outcome.suffix')}`;
        },
    },

    async mounted() {
        await this.fetchModalOptions();
        this.loadConfig();
    },

    methods: {
        async fetchModalOptions() {
            this.taskTypeOptions = await getContactActionOptions();
            this.taskOutcomeOptions = await getTaskOutcomeOptions();
            this.usersOptions = await getGroupMembers();
            this.reminderOptions = await getReminderOptions();
            this.priorityOptions = await getPriorityOptions();
            this.mergeFields = await getMergeFieldsForCB(this.funnelId);
        },

        loadConfig() {
            const { value } = this.cell;

            if (!value) {
                return;
            }

            this.taskType = value.taskType || '';
            this.taskTitle = value.taskTitle || '';
            this.taskBody = value.taskBody || '';
            this.taskAssignToOwner = Number(value.taskAssignToOwner) === 1;
            this.taskAssignToBackup = value.taskAssignToBackup?.toString().replace('L', '') || '';
            this.taskDaysTillDue = Number(value.taskDaysTillDue) || 0;
            this.taskDueAt = value.taskDueAt?.replace(':', '') || this.$options.DEFAULT_MILITARY_TIME;
            this.taskPriority = Number(value.taskPriority) || '';
            this.taskNotifyOwner = Number(value.taskNotifyOwner) === 1;
            this.taskNotify = value.taskNotify?.map((item) => parseInt(item, 10).toString()) || [];
            this.taskReminder = Number(value.taskReminder) || '';
            this.taskOutcome = value.taskOutcome || [];
        },

        closeModal() {
            this.$emit('close-modal');
        },

        updateConfig() {
            if (this.isValid()) {
                this.saveCellValue();
            }
        },

        async closeConfigModal() {
            this.taskOutcomeOptions = await getTaskOutcomeOptions();
            this.showOutcomeConfig = false;
        },

        async openAddNewOutcome(newOutcome) {
            this.outcomeCategoryOptions = await getTaskOutcomeCategoryList();
            this.outcomeToAdd = newOutcome;
            this.showOutcomeEditor = true;
        },

        closeEditor() {
            this.newOutcomeInput = '';
            this.showOutcomeEditor = false;
        },

        async closeEditorAndReload(outcomeId) {
            this.taskOutcomeOptions = await getTaskOutcomeOptions();
            this.taskOutcome.push(`${outcomeId}`);
            this.closeEditor();
        },

        manageTaskOutcomes() {
            this.showOutcomeConfig = true;
            this.newOutcomeInput = '';
        },

        handleInputChange(value) {
            this.newOutcomeInput = value;
        },

        saveCellValue() {
            const cell = { ...this.cell };

            cell.value = cell.value || {};

            cell.value.taskType = this.taskType;
            cell.value.taskTitle = this.taskTitle;
            cell.value.taskBody = this.taskBody;
            cell.value.taskAssignToOwner = this.taskAssignToOwner ? 1 : 0;
            const isValidBackup = this.usersOptions.some((user) => user.value === this.taskAssignToBackup);

            cell.value.taskAssignToBackup = isValidBackup ? `${this.taskAssignToBackup}L` : '';
            cell.value.taskDaysTillDue = Number(this.taskDaysTillDue);
            cell.value.taskDueAt = this.taskDueAt === '' || !this.taskDueAt ? '08:00' : `${this.taskDueAt.slice(0, 2)}:${this.taskDueAt.slice(2)}`;
            cell.value.taskPriority = this.taskPriority === '' ? '' : Number(this.taskPriority);
            cell.value.taskNotifyOwner = this.taskNotifyOwner ? 1 : 0;
            cell.value.taskNotify = (this.taskNotify || []).map((item) => `${item}L`);
            cell.value.taskReminder = this.taskReminder === '' ? '' : Number(this.taskReminder);
            cell.value.taskOutcome = this.taskOutcome;

            this.$emit('update-cell-value', { cell });
        },

        setIsValidTaskTitle() {
            this.invalidTaskTitle = this.taskTitle === '';
        },

        setIsValidTaskType() {
            this.invalidTaskType = this.taskType === '';
        },

        setIsValidTaskBackup() {
            this.invalidTaskBackup = !this.usersOptions.some((user) => user.value === this.taskAssignToBackup);
        },

        setIsValidTaskPriority() {
            this.invalidTaskPriority = this.taskPriority === '';
        },

        isValid() {
            this.setIsValidTaskTitle();
            this.setIsValidTaskType();
            this.setIsValidTaskBackup();
            this.setIsValidTaskPriority();

            return !this.invalidTaskTitle && !this.invalidTaskType && !this.invalidTaskBackup && !this.invalidTaskPriority;
        },
    },
};
</script>

<style lang="scss" scoped>
.create-task-merge-field-input {
    width: -webkit-fill-available;
    max-width: px-to-rem(590);
}
.merge-field-container {
    display: flex;
}

.merge-field-icon {
    color: $color-blue-700;
}
.create-task-due {
    display: grid;
    grid-template-areas: 'createtaskduetitle createtaskduetitle createtaskduetitle' 'createtaskdaysdue createtaskduelabel createtaskdueat';
    grid-template-columns: px-to-rem(75) px-to-rem(250) px-to-rem(125);
    grid-row-gap: $spacing-100;
    grid-column-gap: $spacing-100;
    grid-template-rows: $spacing-250 $spacing-500;
    padding-bottom: px-to-rem(22);
    align-items: baseline;
}
.create-task-due-title {
    grid-area: createtaskduetitle;
    font-weight: 500;
    font-size: $font-size-sm;
    line-height: $line-height-sm;
    color: $color-ink-900;
}
.create-task-days-due {
    grid-area: createtaskdaysdue;
}
.create-task-due-label {
    grid-area: createtaskduelabel;
}
.create-task-due-at {
    grid-area: createtaskdueat;
}

.create-task-notify-owner,
.create-task-assign-owner {
    padding: 0 0 $spacing-300 0;
}

.error-message {
    padding-bottom: $spacing-300;
    margin-top: -$spacing-150;
    color: $color-red-800;
    font-weight: $font-weight-regular;
    font-size: $font-size-xs;
    line-height: $line-height-sm;
}
:deep(div.create-task-due-at > div.date-picker-container > label) {
    top: px-to-rem(4);
}
:deep(div.create-task-due-at.has-selected > div.date-picker-container > label) {
    top: 0;
}
</style>
